/**
 * English (Base) translations
 * This is the complete English translation file
 */
export const en = {
  // Header & Navigation
  nav: {
    home: "Home",
    services: "Services",
    gallery: "Gallery",
    reviews: "Reviews",
    contact: "Contact",
    login: "Login",
    register: "Register",
    orderService: "Order Service",
    profile: "Profile",
    logout: "Logout"
  },
  // Hero
  hero: {
    subtitle: "Premium Cleaning Services",
    title1: "Professional",
    title2: "Cleaning Services",
    description: "Expert yacht, residential, and construction cleaning on the French Riviera. Excellence, discretion, and eco-conscious care.",
    cta: "Order Service",
    services: "Our Services",
    welcome: "Welcome back"
  },
  // About
  about: {
    subtitle: "About Us",
    title: "Excellence in Cleaning Services",
    description1: "Shylov Cleaning provides premium cleaning services for yachts, residences, and construction sites along the French Riviera. Based in Nice, Cannes, Monaco, and Cagnes-sur-Mer.",
    description2: "Our team delivers meticulous care using eco-friendly products that protect both your property and the environment.",
    stats: { 
      clients: "Happy Clients", 
      satisfaction: "Satisfaction", 
      experience: "Years Experience",
      yachts: "Yachts Cleaned"
    },
    features: {
      expertise: { title: "Trusted Expertise", desc: "Years of professional cleaning experience" },
      eco: { title: "Eco-Friendly", desc: "Environmentally conscious cleaning products" },
      flexible: { title: "24/7 Service", desc: "Available around the clock for your needs" },
      quality: { title: "Premium Quality", desc: "Meticulous attention to every detail" }
    }
  },
  // Services
  services: {
    subtitle: "What We Offer",
    title: "Our Services",
    description: "Comprehensive cleaning services tailored to your needs.",
    viewAll: "View All Services",
    bookThis: "Book This Service",
    learnMore: "Learn More",
    whatsIncluded: "What's Included",
    customTitle: "Looking for a Specific Service?",
    customDesc: "Get in touch to discuss your requirements.",
    customCta: "Inquiries",
    pageTitle: "Our Professional Services",
    pageDesc: "Yacht cleaning, residential cleaning, and post-construction cleanup on the French Riviera.",
    features: {
      eco: "Eco-Friendly Products",
      available: "24/7 Available",
      fivestar: "5-Star Service",
      riviera: "French Riviera & Beyond"
    },
    tags: {
      exterior: "Exterior",
      interior: "Interior",
      deck: "Deck",
      polishing: "Polishing",
      apartments: "Apartments",
      villas: "Villas",
      offices: "Offices",
      postRenovation: "Post-Renovation",
      debrisRemoval: "Debris Removal"
    },
    categories: {
      yachts: "Yacht Cleaning",
      residential: "Residential Cleaning",
      construction: "Construction Cleanup"
    },
    descriptions: {
      yachts: "Expert yacht cleaning: exterior wash, interior detailing, deck maintenance, and polishing.",
      residential: "Professional home cleaning: apartments, villas, offices. Deep cleaning and regular maintenance.",
      construction: "Post-renovation cleanup: debris removal, dust elimination, floor washing. Move-in ready service."
    },
    discount: {
      title: "Loyalty Discount",
      description: "Regular customers enjoy special pricing! Contact us for personalized offers.",
      cta: "Get Your Discount"
    }
  },
  // Why Us
  whyUs: {
    subtitle: "Why Choose Us",
    title: "The Shylov Difference",
    description: "We don't just clean—we care. Excellence and attention to detail set us apart.",
    yearsLabel: "Years of Excellence",
    reasons: {
      team: { title: "Professional Team", desc: "Trained experts dedicated to cleaning excellence." },
      eco: { title: "Eco-Friendly Products", desc: "Marine-safe, biodegradable cleaning solutions." },
      marina: { title: "Multiple Locations", desc: "Service in Nice, Cannes, Monaco, Cagnes-sur-Mer." },
      fast: { title: "24/7 Available", desc: "Round the clock service for your convenience." }
    }
  },
  // CTA
  cta: {
    subtitle: "Ready to Get Started?",
    title: "Your Property Deserves the Best",
    description: "Contact us today for a free consultation.",
    quote: "Order Service",
    call: "Call"
  },
  // Contact
  contact: {
    subtitle: "Get in Touch",
    title: "Contact Us",
    description: "Ready for premium cleaning services? Get in touch for a free consultation.",
    letsTalk: "Let's Talk",
    email: "Email",
    phone: "Phone",
    location: "Locations",
    locationValue: "Nice • Cannes • Monaco • Cagnes-sur-Mer",
    locationDetail: "Serving the entire French Riviera",
    serviceArea: "French Riviera Coverage",
    hours: "Working Hours",
    hoursValue: "24/7 Available",
    hoursDetail: "We work around the clock",
    freeConsultation: "Free Consultation",
    freeConsultationDesc: "Not sure which service you need? Contact us for a free consultation.",
    formTitle: "Request a Quote",
    formDesc: "Fill out the form and we'll get back to you within 24 hours.",
    form: {
      name: "Full Name",
      namePlaceholder: "Your name",
      email: "Email Address",
      emailPlaceholder: "your@email.com",
      phone: "Phone Number",
      phonePlaceholder: "+33 6 XX XX XX XX",
      serviceCategory: "Service Category",
      serviceCategoryPlaceholder: "Select category",
      serviceType: "Service Type",
      serviceTypePlaceholder: "Select service type",
      firstSelectCategory: "First select a category",
      location: "Location",
      locationPlaceholder: "Nice, Monaco, Cannes...",
      selectTime: "Select time",
      openCalendar: "Open Calendar",
      hideCalendar: "Hide Calendar",
      preferredDate: "Preferred Date",
      preferredTime: "Preferred Time",
      message: "Message",
      messagePlaceholder: "Tell us more about your needs...",
      messageRequired: "Please describe your cleaning needs",
      submit: "Send Request",
      sending: "Sending...",
      success: "Request sent successfully!",
      successDesc: "We will contact you shortly.",
      error: "Failed to send",
      errorDesc: "Please try again or contact us directly."
    },
    serviceCategories: ["Yacht Cleaning", "Residential Cleaning", "Construction Cleaning"],
    locations: ["Nice", "Cannes", "Monaco", "Cagnes-sur-Mer", "Other"],
    yachtLengths: ["Under 10m", "10-15m", "15-20m", "20-30m", "30-50m", "Over 50m"],
    yachtTypes: ["Sailing Yacht", "Motor Yacht", "Catamaran", "Superyacht", "Other"],
    constructionTypes: ["New Build", "Renovation", "Apartment Remodel", "Office Fit-out", "Commercial Space"],
    constructionStages: ["During Work", "After Renovation", "Final Cleanup", "Move-in Ready"],
    express: { title: "Express Cleaning", priority: "PRIORITY", description: "Need urgent cleaning? Select this option for priority processing within 24 hours. Additional fee applies for express service.", extraFee: "Additional fee applies" },
    yacht: { title: "Yacht Details", length: "Yacht length", type: "Yacht type", selectLength: "Select length...", selectType: "Select type..." },
    residential: { title: "Property Details", area: "Area (sq m)", rooms: "Rooms", bathrooms: "Bathrooms", hasBalcony: "Has balcony/terrace", hasPets: "Has pets", areaPlaceholder: "e.g. 80" },
    construction: { title: "Post-Renovation Details", description: "We clean after construction: floor washing, debris removal, dust cleaning.", projectType: "Project type", cleanupStage: "Cleanup stage", selectType: "Select type...", selectStage: "Select stage...", debrisRemoval: "Requires debris removal", areaPlaceholder: "e.g. 200" },
    serviceAreas: {
      title: "Service Areas",
      description: "We provide professional cleaning services across the French Riviera and beyond",
      alsoServing: "Also Serving",
      moreAreas: "+ More areas on request",
      mainOffice: "Main Office",
      portCannes: "Port de Cannes",
      portHercule: "Port Hercule",
      marina: "Marina"
    }
  },
  // Auth
  auth: {
    login: "Login",
    register: "Register",
    logout: "Logout",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    firstName: "First Name",
    lastName: "Last Name",
    phone: "Phone Number",
    gender: "Gender",
    genderOptions: { male: "Male", female: "Female", other: "Other", preferNot: "Prefer not to say" },
    country: "Country",
    city: "City",
    address: "Address (Optional)",
    createAccount: "Create Account",
    alreadyHave: "Already have an account?",
    dontHave: "Don't have an account?",
    loginHere: "Login here",
    registerHere: "Register here",
    forgotPassword: "Forgot password?",
    loginSuccess: "Login successful!",
    registerSuccess: "Account created successfully!",
    loginError: "Invalid email or password",
    registerError: "Registration failed. Please try again.",
    passwordMismatch: "Passwords do not match",
    requiredField: "This field is required",
    invalidEmail: "Invalid email address",
    welcomeBack: "Welcome Back",
    createYourAccount: "Create Your Account",
    joinUs: "Join Shylov Cleaning and enjoy premium services",
    verifyPhoneLabel: "Verify phone number via SMS",
    verifyPhoneDesc: "Optional: Receive a verification code to confirm your phone number",
    verificationSent: "Verification code sent!"
  },
  // Phone Verification
  verifyPhone: {
    title: "Verify Your Phone",
    subtitle: "Enter the 6-digit code sent to",
    verify: "Verify",
    resend: "Resend Code",
    resendIn: "Resend in",
    skip: "Skip for now",
    success: "Phone Verified!",
    redirecting: "Redirecting to dashboard...",
    invalidCode: "Invalid verification code",
    error: "Verification failed"
  },
  // Footer
  footer: {
    description: "Premium cleaning services on the French Riviera. Professional care for yachts, homes, and construction sites.",
    navigation: "Navigation",
    ourServices: "Our Services",
    contactUs: "Contact Us",
    rights: "All rights reserved.",
    tagline: "Professional Cleaning on the French Riviera",
    terms: "Terms & Conditions",
    stayUpdated: "Stay Updated",
    newsletterDescription: "Subscribe to our newsletter for exclusive offers and cleaning tips",
    subscribe: "Subscribe",
    emailPlaceholder: "Enter your email",
    subscribeSuccess: "Thank you for subscribing!",
    subscribeError: "Failed to subscribe",
    dashboard: "Dashboard"
  },
  // Common
  common: {
    backToHome: "Back to Home",
    loading: "Loading...",
    submit: "Submit",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    back: "Back",
    next: "Next",
    view: "View",
    viewFull: "View Full",
    search: "Search",
    filter: "Filter",
    confirmDelete: "Are you sure you want to delete?",
    success: "Success",
    error: "Error"
  },
  // Admin
  admin: {
    title: "Admin Panel",
    dashboard: "Dashboard",
    analytics: "Analytics",
    users: "Users",
    orders: "Orders",
    reviews: "Reviews",
    liveChat: "Live Chat",
    totalUsers: "Total Users",
    totalOrders: "Total Orders",
    pendingOrders: "Pending Orders",
    accessDenied: "You don't have permission to access this page",
    adminRequired: "Admin Access Required",
    makeAdmin: "Make admin",
    removeAdmin: "Remove admin",
    deleteUser: "Delete user",
    userDeleted: "User deleted",
    updateFailed: "Update failed",
    reviewDeleted: "Review deleted",
    chatTip: "Users can click 'Talk to operator' in the chat widget to request live support.",
    chatDescription: "When users request to speak with an operator, their chat sessions will appear here.",
    tip: "Tip",
    manageDescription: "Manage users, orders, reviews and content",
    refresh: "Refresh",
    inProgress: "In Progress",
    completed: "Completed",
    newsletterSubs: "Newsletter Subs",
    conversionRate: "Conversion Rate",
    avgRating: "Avg Rating",
    expressOrders: "Express Orders",
    express: "EXPRESS",
    countries: "Countries",
    uniqueCountries: "unique countries",
    ordersByService: "Orders by Service Type",
    ordersByStatus: "Orders by Status",
    topLocations: "Top Locations",
    ordersLast7Days: "Orders Last 7 Days",
    usersByCountry: "Users by Country",
    user: "User",
    contact: "Contact",
    location: "Location",
    role: "Role",
    actions: "Actions",
    adminRole: "Admin",
    userRole: "User",
    monthlyTrends: "Monthly Trends (Last 6 Months)",
    newUsers: "New Users",
    locationDate: "Location & Date",
    notSpecified: "Not specified",
    at: "at",
    created: "Created",
    message: "Message",
    updateStatus: "Update Status",
    adminGranted: "Admin rights granted",
    adminRemoved: "Admin rights removed",
    statusUpdated: "Order status updated",
    noOrders: "No orders yet",
    noReviews: "No reviews yet",
    adminEmailSent: "Admin notification sent",
    adminEmailNotSent: "Admin notification not sent",
    customerEmailSent: "Customer confirmation sent",
    customerEmailNotSent: "Customer confirmation not sent",
    status: {
      pending: "PENDING",
      accepted: "ACCEPTED",
      in_progress: "IN PROGRESS",
      completed: "COMPLETED",
      cancelled: "CANCELLED"
    }
  },
  // Dashboard
  dashboard: {
    myOrders: "My Orders",
    notifications: "Notifications",
    profile: "Profile",
    settings: "Settings",
    logout: "Logout",
    newsletter: "Newsletter",
    sendNewsletter: "Send Newsletter",
    subject: "Subject Line",
    content: "Message Content",
    subscribers: "subscribers",
    sendUpdates: "Send updates and promotions to",
    noOrders: "No orders yet",
    orderNow: "Order Now",
    editProfile: "Edit Profile",
    backToDashboard: "Back to Dashboard",
    soundEffects: "Sound Effects",
    soundsEnabled: "Subtle sounds are enabled",
    soundsMuted: "Sounds are muted",
    profileUpdated: "Profile updated successfully!",
    photoUploaded: "Photo uploaded successfully!",
    readyToBook: "Ready to Book?",
    contactNow: "Contact us now for a free consultation and quote",
    newsletterSuccess: "Newsletter sent successfully!",
    newsletterError: "Failed to send newsletter",
    fillSubjectContent: "Please fill in subject and content",
    orderStatusUpdated: "Order status updated",
    orderStatusUpdateFailed: "Failed to update status",
    allNotificationsRead: "All notifications marked as read"
  },
  // Forms
  forms: {
    yourRating: "Your Rating",
    serviceType: "Service Type",
    title: "Title",
    review: "Review",
    beforePhoto: "Before Photo",
    afterPhoto: "After Photo",
    optional: "optional",
    required: "required",
    dragDrop: "Drag & drop or click to upload",
    shareExperience: "Share your experience with our services",
    backToReviews: "Back to Reviews",
    select: "Select...",
    selectCountry: "Select country...",
    selectService: "Select service...",
    enterEmail: "your@email.com",
    enterPassword: "••••••••",
    enterPhone: "+33 6 XX XX XX XX",
    enterTitle: "e.g., Excellent service!",
    enterReview: "Tell us about your experience...",
    typeMessage: "Type your message...",
    reviewTitle: "Review Title",
    yourReview: "Your Review",
    beforeAfterPhotos: "Before & After Photos",
    uploadPhotosHint: "Upload photos to show the transformation (recommended)",
    orEnterUrls: "Or enter image URLs manually",
    beforeImageUrl: "Before image URL...",
    afterImageUrl: "After image URL...",
    newsletterSubject: "Subject Line",
    newsletterContent: "Message Content"
  },
  // Reviews Page
  reviewsPage: {
    customerFeedback: "Customer Feedback",
    latestReviews: "Latest Reviews",
    readWhatClients: "Read what our clients say about our professional cleaning services",
    writeReview: "Write a Review",
    writeReviewTitle: "Write a Review | Shylov Cleaning",
    loginToReview: "to leave a review",
    before: "Before",
    after: "After",
    verified: "Verified",
    totalReviews: "Total Reviews",
    recentReviews: "Recent Reviews",
    satisfiedClients: "Satisfied Clients",
    availableService: "Available Service",
    available247: "24/7"
  },
  // Service Types for Reviews (maps DB values to translations)
  serviceTypes: {
    "Exterior Yacht Cleaning": "Exterior Yacht Cleaning",
    "Interior Yacht Cleaning": "Interior Yacht Cleaning",
    "Deck & Hull Wash": "Deck & Hull Wash",
    "Polishing & Protection": "Polishing & Protection",
    "Apartment Cleaning": "Apartment Cleaning",
    "Villa & House Cleaning": "Villa & House Cleaning",
    "Office Cleaning": "Office Cleaning",
    "Post-Renovation Cleaning": "Post-Renovation Cleaning",
    "Construction Site Cleanup": "Construction Site Cleanup"
  }
};

export default en;
